import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService, User } from '@/services/authService';
import { STAFF_SESSION_INVALID_EVENT } from '@/services/staffSession';

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  canAccessClinic: (clinicId: string) => boolean;
  canManageUsers: () => boolean;
  canAccessAdminPanel: () => boolean;
  canAccessPatients: () => boolean;
  canAccessInventory: () => boolean;
  canSeeAllTasks: () => boolean;
  canEditTaskDeadline: () => boolean;
  canDeleteTasks: () => boolean;
  canManageClinic: (clinicId: string) => boolean;
  getUserAccessLevel: () => 'none' | 'user' | 'clinic_manager' | 'operations' | 'admin';
  getUserStaffId: () => string | undefined;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Check for saved session on mount. The server cookie decides whether the
  // user is signed in; restoreSession also makes sure this browser holds a live
  // staff session token (issuing one against the cookie if it does not), since
  // patient data is reached with that token.
  useEffect(() => {
    let cancelled=false;
    authService.restoreSession().then(current=>{if(!cancelled)setUser(current);}).catch(()=>{if(!cancelled)setUser(null);}).finally(()=>{if(!cancelled)setLoading(false);});
    return ()=>{cancelled=true;};
  }, []);

  // Any patient service that gets its token (or the public key) refused drops
  // the token and fires this; sign the user out so the app shows the login
  // screen instead of rendering with data it can no longer fetch (PatientsView
  // would otherwise quietly substitute preview data).
  useEffect(() => {
    const onInvalid = () => setUser(null);
    window.addEventListener(STAFF_SESSION_INVALID_EVENT, onInvalid);
    return () => window.removeEventListener(STAFF_SESSION_INVALID_EVENT, onInvalid);
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    const { user: loggedInUser, error } = await authService.login({ email, password });
    
    if (loggedInUser && !error) {
      setUser(loggedInUser);
      return true;
    }
    
    return false;
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
  };

  const canAccessClinic = (clinicId: string): boolean => {
    return authService.canAccessClinic(user, clinicId);
  };

  const canManageUsers = (): boolean => {
    return authService.canManageUsers(user);
  };

  const canAccessAdminPanel = (): boolean => {
    return authService.canAccessAdminPanel(user);
  };

  const canAccessPatients = (): boolean => {
    return authService.canAccessPatients(user);
  };

  const canAccessInventory = (): boolean => {
    return authService.canAccessInventory(user);
  };

  const canSeeAllTasks = (): boolean => {
    return authService.canSeeAllTasks(user);
  };

  const canEditTaskDeadline = (): boolean => {
    return authService.canEditTaskDeadline(user);
  };

  const canDeleteTasks = (): boolean => {
    return authService.canDeleteTasks(user);
  };

  const canManageClinic = (clinicId: string): boolean => {
    return authService.canManageClinic(user, clinicId);
  };

  const getUserAccessLevel = (): 'none' | 'user' | 'clinic_manager' | 'operations' | 'admin' => {
    return authService.getUserAccessLevel(user);
  };

  const getUserStaffId = (): string | undefined => {
    return user?.staffId;
  };

  if (loading) {
    return null; // Or a loading spinner
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isAuthenticated: !!user,
        canAccessClinic,
        canManageUsers,
        canAccessAdminPanel,
        canAccessPatients,
        canAccessInventory,
        canSeeAllTasks,
        canEditTaskDeadline,
        canDeleteTasks,
        canManageClinic,
        getUserAccessLevel,
        getUserStaffId
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
